import { useRef } from 'react'
import { FormProvider } from 'react-hook-form'
import cn from 'classnames'
import { Button, ButtonStyles, Typography } from '@grammarly/badger-ui'

import { TextInput } from '../../../inputs/text-input'
import { FormProvider as UILibFormProvider, RequiredOrOptional } from '../../../ui-lib/form/form-context'
import { useAIDetector } from '../ai-detector-context'
import { AnnounceLoading } from '../../../section/go-templates-tool/form'
import { errorStyle } from '../../../ui-lib/form/error-text'

import { AIDetectorLoading } from '../loading'
import { AIDetectorErrorOrCounter } from '../error-or-counter'
import { AIDetectorMobileOutput } from './output'
import { aiDetectorQuestionStructure, aiDetectorYOffset } from '../utils'
import { AIDetectorTextLimitModal } from '../modals'

import styles from './ai-detector-mobile.module.scss'

const numCharsPerLoadingLine = 40
const formId = `form-ai-detector-mobile`
const { C4 } = Typography

export const AIDetectorMobile = () => {
  const { methods, aiDetectorState, aiPercent, onSubmit, onClear, onFormChangeCapture, onFormClickCapture, onChange } =
    useAIDetector()
  const { handleSubmit, watch } = methods
  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  const onFocus = () => {
    if (textAreaRef.current) {
      const y = textAreaRef.current.getBoundingClientRect().top + window.scrollY + aiDetectorYOffset
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }

  const watchText = watch(aiDetectorQuestionStructure.fields.name, '')
  const hasText = (watchText?.length || 0) > 0
  const numLoadingLines = Math.ceil(watchText.length / numCharsPerLoadingLine)
  const isLoading = aiDetectorState === 'loading'

  return (
    <div className={styles.toolContainer}>
      <div aria-live="polite" className={cn(styles.formContainer)}>
        <FormProvider {...methods}>
          <UILibFormProvider value={{ formId, requiredOrOptionalIndicator: RequiredOrOptional.Optional }}>
            <form
              id={formId}
              className={cn('font-family-inter', styles.form)}
              onSubmit={handleSubmit(onSubmit)}
              aria-live="polite"
              aria-relevant="additions"
              onChangeCapture={onFormChangeCapture}
              onClickCapture={onFormClickCapture}
              method="post"
            >
              <div className={styles.inputContainer}>
                <TextInput
                  structure={aiDetectorQuestionStructure}
                  className={cn(styles.textarea, isLoading && styles.textareaHidden)}
                  labelClassName={styles.labelHidden}
                  hideErrorText
                  hasLongformText
                  ref={textAreaRef}
                  onFocus={onFocus}
                  onChange={onChange}
                />
                <div className={cn(styles.loadingContainer, !isLoading && styles.loadingContainerHidden)}>
                  <AIDetectorLoading numLoadingLines={numLoadingLines} labelOffset={120} />
                </div>
                {hasText && (
                  <Button
                    padding="compact"
                    className={cn(ButtonStyles.TransparentButtonGrayText, styles.clearTextButton)}
                    type="button"
                    status={aiDetectorState === 'loading' ? 'disabled' : 'enabled'}
                    onClick={onClear}
                  >
                    Clear X
                  </Button>
                )}
              </div>

              <div className={styles.buttonContainer}>
                <Button
                  padding="compact"
                  className={cn(
                    aiDetectorState === 'done'
                      ? ButtonStyles.WhiteButtonGreenTextWithBorder
                      : ButtonStyles.GreenButtonWhiteText,
                    styles.checkTextButton
                  )}
                  type="submit"
                  status={aiDetectorState === 'loading' ? 'loading' : 'enabled'}
                >
                  Check text
                </Button>
                <div className={styles.rightContainer}>
                  {aiDetectorState === 'error' && hasText ? (
                    <C4 as="span" className={errorStyle}>
                      Something went wrong. Please try again later.
                    </C4>
                  ) : (
                    <AIDetectorErrorOrCounter structure={aiDetectorQuestionStructure} />
                  )}
                </div>
              </div>
              <AnnounceLoading isLoading={aiDetectorState === 'loading'} />
              <AIDetectorTextLimitModal structure={aiDetectorQuestionStructure} />
            </form>
          </UILibFormProvider>
        </FormProvider>
      </div>
      <AIDetectorMobileOutput aiPercent={aiPercent} />
    </div>
  )
}
